import { useEffect, useRef, useState } from "react";

const MAGNET = "a, button, [role='button'], input[type='submit'], summary";
const TRAIL_MS = 380;

function canUse() {
  if (typeof window === "undefined") return false;
  if (!window.matchMedia("(pointer: fine)").matches) return false;
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return false;
  return true;
}

function cursorRgb() {
  return getComputedStyle(document.documentElement).getPropertyValue("--eden-cursor").trim().replace(/\s+/g, ", ") || "255, 85, 0";
}

function drawTrail(ctx, pts, now, w, h) {
  ctx.clearRect(0, 0, w, h);
  if (pts.length < 2) return;
  const rgb = cursorRgb();
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1], b = pts[i];
    const life = 1 - (now - b.t) / TRAIL_MS;
    if (life <= 0) continue;
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(b.x, b.y);
    ctx.shadowBlur = 14 * life;
    ctx.shadowColor = `rgba(${rgb}, 0.9)`;
    ctx.strokeStyle = `rgba(${rgb}, ${0.85 * life})`;
    ctx.lineWidth = 1 + 2.2 * life;
    ctx.stroke();
  }
  ctx.shadowBlur = 0;
}

export function CustomCursor() {
  const [enabled] = useState(canUse);
  const arrow = useRef(null);
  const canvas = useRef(null);

  useEffect(() => {
    if (!enabled) return;
    document.documentElement.classList.add("has-custom-cursor");
    const ctx = canvas.current.getContext("2d");
    const pos = { x: -100, y: -100 };
    const pts = [];
    let magnet = null;
    let hidden = true;
    let raf;
    let w = 0, h = 0;

    const resize = () => { w = canvas.current.width = window.innerWidth; h = canvas.current.height = window.innerHeight; };
    const setMagnet = (el) => {
      magnet?.classList.remove("magnet-active");
      magnet?.style.removeProperty("transform");
      magnet = el;
      magnet?.classList.add("magnet-active");
      arrow.current?.classList.toggle("hover", !!magnet);
    };
    const onMove = (e) => {
      pos.x = e.clientX;
      pos.y = e.clientY;
      pts.push({ x: pos.x, y: pos.y, t: performance.now() });
      if (hidden) { hidden = false; arrow.current?.classList.add("visible"); }
      const el = e.target.closest?.(MAGNET);
      if (el !== magnet) setMagnet(el);
      if (magnet) {
        const r = magnet.getBoundingClientRect();
        const dx = (e.clientX - (r.left + r.width / 2)) * 0.18;
        const dy = (e.clientY - (r.top + r.height / 2)) * 0.18;
        magnet.style.transform = `translate(${dx}px, ${dy}px) scale(1.04)`;
      }
    };
    const onLeave = () => { hidden = true; arrow.current?.classList.remove("visible"); setMagnet(null); };
    const onDown = () => arrow.current?.classList.add("down");
    const onUp = () => arrow.current?.classList.remove("down");

    const tick = () => {
      const now = performance.now();
      while (pts.length && now - pts[0].t > TRAIL_MS) pts.shift();
      if (arrow.current) arrow.current.style.transform = `translate(${pos.x}px, ${pos.y}px)`;
      drawTrail(ctx, hidden ? [] : pts, now, w, h);
      raf = requestAnimationFrame(tick);
    };

    resize();
    window.addEventListener("resize", resize);
    window.addEventListener("mousemove", onMove, { passive: true });
    document.addEventListener("mouseleave", onLeave);
    window.addEventListener("mousedown", onDown);
    window.addEventListener("mouseup", onUp);
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseleave", onLeave);
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("mouseup", onUp);
      setMagnet(null);
      document.documentElement.classList.remove("has-custom-cursor");
    };
  }, [enabled]);

  if (!enabled) return null;
  return (
    <div aria-hidden="true" data-testid="custom-cursor">
      <canvas ref={canvas} className="cursor-trail-canvas" />
      <svg ref={arrow} className="cursor-arrow" width="26" height="30" viewBox="0 0 26 30" fill="none">
        <path d="M2 2 L2 24 L8 18.5 L12.5 28 L17 26 L12.5 16.8 L20.5 16.8 Z" fill="rgb(var(--eden-cursor))" stroke="#0b0b0d" strokeWidth="2" strokeLinejoin="round" />
      </svg>
    </div>
  );
}
